import { useCallback, useMemo } from 'react'
import { usePathname } from 'next/navigation'

import { buildDocsDocumentationHref } from '@common/utils/buildDocsDocumentationHref'
import { buildQuickStartArticleUrlPath } from '@common/utils/buildQuickStartArticleUrlPath'
import { getDocsExternalUrl } from '@common/utils/getDocsExternalUrl'
import { isDocsDocumentationPath } from '@common/utils/isDocsDocumentationPath'

import {
  ACADEMY_MENU_ROUTES,
  DEFAULT_QUICKSTART_SECTIONS,
  HIDDEN_MOBILE_SECTION_PATHS,
} from '../academy-mobile-menu.const'
import type { MobileNavigationState, UseMobileNavigationParams } from '../academy-mobile-menu.type'
import type { TutorialSection } from '../types'

export type { MobileNavigationState }

export function useMobileNavigation({
  tutorialSections,
  categoryTree = [],
  rootArticles = [],
  onNavigationClick,
  onClose,
  routeProfile = 'academy',
}: UseMobileNavigationParams): MobileNavigationState {
  const pathname = usePathname()
  const docsExternalUrl = routeProfile === 'academy' ? getDocsExternalUrl() : ''

  const documentationHomePath =
    routeProfile === 'docs'
      ? '/'
      : docsExternalUrl
        ? buildDocsDocumentationHref(docsExternalUrl)
        : ACADEMY_MENU_ROUTES.documentation

  const documentationBasePath = routeProfile === 'docs' ? '' : ACADEMY_MENU_ROUTES.documentation

  const isDocumentationPage =
    docsExternalUrl && routeProfile === 'academy'
      ? false
      : routeProfile === 'docs'
        ? isDocsDocumentationPath(pathname ?? '')
        : (pathname?.startsWith(ACADEMY_MENU_ROUTES.documentation) ?? false)
  const isQuickStartPage = pathname?.startsWith(ACADEMY_MENU_ROUTES.quickStart) ?? false
  const isLearningPage = isQuickStartPage
  const isSubpage = isLearningPage || isDocumentationPage
  const hasTutorialSections = tutorialSections.length > 0

  const quickStartSectionsToShow = useMemo(
    () =>
      (tutorialSections.length > 0 ? tutorialSections : DEFAULT_QUICKSTART_SECTIONS).filter(
        (section) => !HIDDEN_MOBILE_SECTION_PATHS.has(section.path)
      ),
    [tutorialSections]
  )

  const handleNavigationClick = useCallback(
    (url: string) => {
      onNavigationClick?.(url)
      onClose()
    },
    [onNavigationClick, onClose]
  )

  const handleSectionClick = useCallback(
    (section: TutorialSection, basePath: string) => {
      if (basePath === documentationBasePath) {
        const slug = section.path.replace(/^docs\//, '').replace(/\.(md|mdx)$/, '')
        const href = documentationBasePath ? `${documentationBasePath}/${slug}` : `/${slug}`
        handleNavigationClick(href)
      } else {
        const slug = buildQuickStartArticleUrlPath(section)
        handleNavigationClick(`${basePath}/${slug}`)
      }
    },
    [handleNavigationClick, documentationBasePath]
  )

  return {
    isDocumentationPage,
    isQuickStartPage,
    isLearningPage,
    isSubpage,
    hasTutorialSections,
    hasQuickStartItems: quickStartSectionsToShow.length > 0,
    documentationHomePath,
    documentationBasePath,
    quickStartSectionsToShow,
    quickStartCategoryTree: categoryTree,
    documentationCategoryTree: categoryTree,
    rootArticles,
    handleNavigationClick,
    handleSectionClick,
  }
}
